import { InputDecimal } from '@/components/_common';
import AreasInfiniteSelectField from '@/components/_common/AreasInfiniteSelectField';
import ManufacturerColumnFilter from '@/components/_common/Manufacturer/ManufacturerColumnFilter';
import ManufacturerInfiniteSelectField from '@/components/_common/ManufacturerInfiniteSelectField';
import StatusTag from '@/components/_common/StatusTag';
import SubAreasColumnFilter from '@/components/_common/SubAreasColumnFilter';
import SubGroupColumnFilter from '@/components/_common/SubGroup/SubGroupColumnFilter';
import SubGroupsSelectField from '@/components/_common/SubGroupsSelectField';
import {
  NewCiliaItem,
  UpdatedCiliaItem,
} from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import {
  WorkOrderItemType,
  workorderTypeColor,
} from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { Table, Typography } from 'antd';
import { useEffect, useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';

interface Props {
  data: UpdatedCiliaItem[];
  setUpdateData: React.Dispatch<
    React.SetStateAction<{
      updateItems: UpdatedCiliaItem[];
      newItems: NewCiliaItem[];
    } | null>
  >;
  setSelectedUpdateItems: React.Dispatch<
    React.SetStateAction<UpdatedCiliaItem[]>
  >;
}

export default function UpdateItemsList({
  data,
  setUpdateData,
  setSelectedUpdateItems,
}: Props) {
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [filterGroupOpen, setFilterGroupOpen] = useState(false);
  const [filterManufactureOpen, setFilterManufactureOpen] = useState(false);
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  function handleChange(
    field: keyof UpdatedCiliaItem,
    index: number,
    value?: string | number,
  ) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const updatedItems = [...prev.updateItems];
      updatedItems[index] = {
        ...updatedItems[index],
        [field]: value,
      };

      const selected = updatedItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedUpdateItems(selected);
      return {
        ...prev,
        updateItems: updatedItems,
      };
    });
  }

  function setSubGroupId(subGroupId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const updatedItems = prev.updateItems.map((item) =>
        !item.itemId
          ? {
              ...item,
              subGroupId:
                item.type === WorkOrderItemType.Part ? subGroupId : undefined,
            }
          : item,
      );

      const selected = updatedItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedUpdateItems(selected);
      return {
        ...prev,
        updateItems: updatedItems,
      };
    });

    setFilterGroupOpen(false);
  }

  function setManufacturerId(manufacturerId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const updatedItems = prev.updateItems.map((item) =>
        !item.itemId
          ? {
              ...item,
              manufacturerId:
                item.type === WorkOrderItemType.Part
                  ? manufacturerId
                  : undefined,
            }
          : item,
      );

      const selected = updatedItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedUpdateItems(selected);
      return {
        ...prev,
        updateItems: updatedItems,
      };
    });

    setFilterManufactureOpen(false);
  }

  function setSubAreaId(subAreaId: number) {
    setUpdateData((prev) => {
      if (!prev) return prev;

      const updatedItems = prev.updateItems.map((item) => ({
        ...item,
        subAreaId,
      }));

      const selected = updatedItems.filter((_, index) =>
        selectedRowKeys.includes(String(index)),
      );

      setSelectedUpdateItems(selected);
      return {
        ...prev,
        updateItems: updatedItems,
      };
    });

    setFilterDropdownOpen(false);
  }

  useEffect(() => {
    const selected = selectedRowKeys.map((key) => data[Number(key)]);
    setSelectedUpdateItems(selected);
  }, [selectedRowKeys]);

  useEffect(() => {
    const allKeys = data.map((_, index) => String(index));
    setSelectedRowKeys(allKeys);
    setSelectedUpdateItems(data);
  }, []);

  return (
    <>
      <Typography.Title level={3} style={{ margin: 0 }}>
        Itens Atualizados
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        Itens já existentes na OS que foram encontrados com diferenças e podem
        ser atualizados.
      </Typography.Paragraph>

      <Table
        dataSource={data}
        pagination={false}
        rowKey={(_, index) => String(index)}
        rowSelection={{
          selectedRowKeys,
          onChange: setSelectedRowKeys,
        }}
        scroll={{ x: true }}
        columns={[
          {
            dataIndex: 'description',
            title: 'Descrição',
            width: 250,
            ellipsis: true,
          },
          {
            dataIndex: 'type',
            title: 'Tipo',
            width: 80,
            ellipsis: true,
            render: (_, item) =>
              item.type === WorkOrderItemType.Part ||
              item.type === WorkOrderItemType.Service ? (
                <StatusTag
                  color={workorderTypeColor[item.type]}
                  label={
                    item.type === WorkOrderItemType.Service ? 'Serviço' : 'Peça'
                  }
                />
              ) : (
                '-'
              ),
          },
          {
            dataIndex: 'subAreaId',
            title: 'Área',
            width: 200,
            filterDropdownOpen,
            onFilterDropdownOpenChange: setFilterDropdownOpen,
            filterDropdown: () => {
              return <SubAreasColumnFilter onChange={setSubAreaId} />;
            },
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, _, index) => (
              <AreasInfiniteSelectField
                value={rowValue}
                style={{ width: 200 }}
                onChange={(value) => handleChange('subAreaId', index, value)}
                queryFilter={{ active: true }}
              />
            ),
          },
          {
            dataIndex: 'subGroupId',
            title: 'Grupo',
            width: 200,
            filterDropdownOpen: filterGroupOpen,
            onFilterDropdownOpenChange: setFilterGroupOpen,
            filterDropdown: () => {
              return <SubGroupColumnFilter onChange={setSubGroupId} />;
            },
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, row, index) => (
              <SubGroupsSelectField
                value={rowValue}
                style={{ width: 200 }}
                disabled={!!row.itemId || row.type == WorkOrderItemType.Service}
                onChange={(value) => handleChange('subGroupId', index, value)}
              />
            ),
          },
          {
            dataIndex: 'manufacturerId',
            title: 'Fabricante',
            width: 200,
            filterDropdownOpen: filterManufactureOpen,
            onFilterDropdownOpenChange: setFilterManufactureOpen,
            filterDropdown: () => {
              return <ManufacturerColumnFilter onChange={setManufacturerId} />;
            },
            filterIcon: <FaExchangeAlt size={14} />,
            render: (rowValue, row, index) => (
              <ManufacturerInfiniteSelectField
                value={rowValue}
                disabled={!!row.itemId || row.type == WorkOrderItemType.Service}
                style={{ width: 200 }}
                onChange={(value) =>
                  handleChange('manufacturerId', index, value)
                }
              />
            ),
          },
          {
            dataIndex: 'unitPrice',
            title: 'Valor unitário',
            width: 150,
            render: (rowValue, _, index) => (
              <InputDecimal
                style={{ width: 200 }}
                value={rowValue}
                onChange={(value) => handleChange('unitPrice', index, value)}
              />
            ),
          },
        ]}
      />
    </>
  );
}
